import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { forgotPasswordBody } from '@pulsepoint/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import type { z } from 'zod';
import { useForgotPassword } from '../../api/auth';
import { ApiError } from '../../api/client';
import { FormTextField } from '../../components/form/FormTextField';
import { AuthLayout } from '../../components/layout/AuthLayout';

type Values = z.input<typeof forgotPasswordBody>;

export function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');
  const form = useForm<Values>({ resolver: zodResolver(forgotPasswordBody), defaultValues: { email: '' } });

  const submit = form.handleSubmit(async (values) => {
    setError('');
    try {
      await forgot.mutateAsync({ email: values.email.trim() });
      setSentTo(values.email.trim());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
    }
  });

  if (sentTo) {
    return (
      <AuthLayout title="Check your email." lead={<>If there is a Pulsepoint account for <b>{sentTo}</b>, we have sent it a link to choose a new password.</>}>
        <Stack spacing={2.5}>
          <Typography color="textSecondary">The link works for one hour. If nothing arrives in a few minutes, check your junk folder.</Typography>
          <Button component={RouterLink} to="/sign-in" variant="contained" sx={{ alignSelf: 'flex-start' }}>
            Back to sign in
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password." lead="Enter your email and we will send you a link to choose a new one.">
      <Stack component="form" onSubmit={submit} noValidate spacing={2.5}>
        <FormTextField control={form.control} name="email" label="Email" type="email" autoComplete="email" autoFocus />
        {error && <Alert severity="error" role="alert">{error}</Alert>}
        <Button type="submit" variant="contained" disabled={forgot.isPending} sx={{ minHeight: 48 }}>
          {forgot.isPending ? 'Sending…' : 'Send the reset link'}
        </Button>
        <Link component={RouterLink} to="/sign-in" sx={{ alignSelf: 'flex-start' }}>
          Back to sign in
        </Link>
      </Stack>
    </AuthLayout>
  );
}
